// Shared helpers for the multi-offer system used by Codes and Code Generators.
//
// An "offer" bundles a set of rewards (cash, trade tickets, collections, cards,
// boosters and an optional discount). A Code or a CodeGenerator can hold up to
// MAX_OFFERS offers. When a user redeems a code that has more than one offer,
// they are asked to pick the one they want.

export const MAX_OFFERS = 5

export const DISCOUNT_TARGETS = [
	{ label: 'Tout (Casino + Boutique + Marché Noir)', value: 'all' },
	{ label: 'Casino uniquement', value: 'casino' },
	{ label: 'Boutique uniquement', value: 'shop' },
	{ label: 'Marché Noir uniquement', value: 'blackmarket' },
	{ label: 'Collection ciblée (Boutique + Marché Noir)', value: 'collection' },
	{ label: "Tickets d'échange (Boutique)", value: 'tradeTickets' },
]

// Create a blank offer with a default label.
export function createEmptyOffer(index = 0) {
	return {
		name: `Offre ${index + 1}`,
		cash: 0,
		tradeTickets: 0,
		collections: [],
		cards: [],
		boosters: [],
		discount: 0,
		discountTarget: 'all',
		discountCollectionId: null,
	}
}

// Ensure a raw offer object has every expected field with a sane type.
export function normalizeOffer(raw = {}, index = 0) {
	const discount = Number(raw.discount) || 0
	return {
		name: (raw.name && String(raw.name).trim()) || `Offre ${index + 1}`,
		cash: Number(raw.cash) || 0,
		tradeTickets: Number(raw.tradeTickets) || 0,
		collections: Array.isArray(raw.collections) ? [...raw.collections] : [],
		cards: Array.isArray(raw.cards)
			? raw.cards.map(c => ({ id: c.id || null, amount: Number(c.amount) || 1, rarity: c.rarity || 'common' }))
			: [],
		boosters: Array.isArray(raw.boosters)
			? raw.boosters.map(b => ({ id: b.id || null, amount: Number(b.amount) || 1 }))
			: [],
		discount,
		discountTarget: raw.discountTarget || 'all',
		discountCollectionId: raw.discountCollectionId || null,
	}
}

// Build the offers array from a Firestore document's data, with full backward
// compatibility for documents created before the multi-offer system existed.
export function offersFromData(data = {}) {
	if (Array.isArray(data.offers) && data.offers.length > 0) {
		return data.offers.map((o, i) => normalizeOffer(o, i))
	}
	return [legacyOfferFromData(data)]
}

// Synthesize a single offer from the legacy top-level reward fields.
export function legacyOfferFromData(data = {}) {
	// Collections (array form + very old single `collection` field)
	const collections = Array.isArray(data.collections) ? [...data.collections] : []
	if (data.collection && !collections.includes(data.collection)) collections.push(data.collection)

	// Cards (array form + old single `card` field)
	const cards = Array.isArray(data.cards)
		? data.cards.map(c => ({ id: c.id || null, amount: Number(c.amount) || 1, rarity: c.rarity || 'common' }))
		: []
	if (data.card) {
		cards.push({ id: data.card, amount: Number(data.cardAmount) || 1, rarity: data.cardRarity || 'common' })
	}

	// Boosters (array form + old single `booster` field)
	const boosters = Array.isArray(data.boosters)
		? data.boosters.map(b => ({ id: b.id || null, amount: Number(b.amount) || 1 }))
		: []
	if (data.booster) {
		boosters.push({ id: data.booster, amount: Number(data.boosterAmount) || 1 })
	}

	return normalizeOffer({
		name: 'Offre 1',
		cash: data.cash,
		tradeTickets: data.tradeTickets,
		collections,
		cards,
		boosters,
		discount: data.discount,
		discountTarget: data.discountTarget,
		discountCollectionId: data.discountCollectionId,
	}, 0)
}

// Strip the form-only helper fields and persist a clean offers array.
export function offersToData(offers = []) {
	const list = (Array.isArray(offers) && offers.length ? offers : [createEmptyOffer(0)]).map((o, i) => {
		const hasDiscount = o.hasDiscount !== undefined ? o.hasDiscount : (Number(o.discount) || 0) > 0
		const discount = hasDiscount ? (Number(o.discount) || 0) : 0
		const discountTarget = o.discountTarget || 'all'
		return {
			name: (o.name && String(o.name).trim()) || `Offre ${i + 1}`,
			cash: Number(o.cash) || 0,
			tradeTickets: Number(o.tradeTickets) || 0,
			// A discount offer does not also grant collections/cards/boosters.
			collections: hasDiscount ? [] : (Array.isArray(o.collections) ? o.collections.filter(Boolean) : []),
			cards: hasDiscount ? [] : (Array.isArray(o.cards) ? o.cards.filter(c => c && c.id).map(c => ({ id: c.id, amount: Number(c.amount) || 1, rarity: c.rarity || 'common' })) : []),
			boosters: hasDiscount ? [] : (Array.isArray(o.boosters) ? o.boosters.filter(b => b && b.id).map(b => ({ id: b.id, amount: Number(b.amount) || 1 })) : []),
			discount,
			discountTarget,
			discountCollectionId: discountTarget === 'collection' ? (o.discountCollectionId || null) : null,
		}
	})
	return list
}

// Prepare offers for editing in a dialog (adds the `hasDiscount` toggle helper).
export function offersForForm(offers = []) {
	const list = (Array.isArray(offers) && offers.length ? offers : [createEmptyOffer(0)])
	return list.map((o, i) => {
		const n = normalizeOffer(o, i)
		return { ...n, hasDiscount: (Number(n.discount) || 0) > 0 }
	})
}

export function discountTargetLabel(target, collectionId, collections = []) {
	if (target === 'collection') {
		const col = collections.find(c => c.id === collectionId)
		return col ? `Collection « ${col.name} »` : 'Collection ciblée'
	}
	const map = {
		casino: 'Casino',
		shop: 'Boutique',
		blackmarket: 'Marché Noir',
		tradeTickets: "Tickets d'échange (boutique)",
		all: 'Tout',
	}
	return map[target] || target
}

// Human-readable list of the rewards contained in an offer.
// opts: { collections, boosters, cards } arrays of {id, name/number/...}
export function offerRewardLines(offer, opts = {}) {
	if (!offer) return []
	const { collections = [], boosters = [], cards = [] } = opts
	const lines = []

	if (offer.cash > 0) lines.push({ icon: 'mdi-cash', text: `${offer.cash} Card coin(s)` })
	if (offer.tradeTickets > 0) lines.push({ icon: 'mdi-ticket-confirmation', text: `${offer.tradeTickets} ticket(s) d'échange` })

	;(offer.collections || []).forEach(cid => {
		const col = collections.find(c => c.id === cid)
		lines.push({ icon: 'mdi-folder-star', text: `Collection : ${col ? col.name : cid}` })
	})

	;(offer.boosters || []).forEach(b => {
		if (!b.id) return
		const booster = boosters.find(x => x.id === b.id)
		lines.push({ icon: 'mdi-package-variant-closed', text: `${b.amount}× ${booster ? booster.name : b.id}` })
	})

	;(offer.cards || []).forEach(c => {
		if (!c.id) return
		const card = cards.find(x => x.id === c.id)
		const label = card ? `${card.number} - ${card.name}` : c.id
		lines.push({ icon: 'mdi-cards-playing', text: `${c.amount}× ${label} (${rarityLabel(c.rarity)})` })
	})

	if (offer.discount > 0) {
		lines.push({ icon: 'mdi-percent', text: `-${offer.discount}% sur ${discountTargetLabel(offer.discountTarget, offer.discountCollectionId, collections)}` })
	}

	if (!lines.length) lines.push({ icon: 'mdi-gift-outline', text: 'Récompense mystère' })
	return lines
}

// One-line compact summary, e.g. for table cells.
export function offerShortSummary(offer) {
	if (!offer) return '—'
	const parts = []
	if (offer.discount > 0) parts.push(`-${offer.discount}%`)
	if (offer.cash > 0) parts.push(`${offer.cash} CC`)
	if (offer.tradeTickets > 0) parts.push(`${offer.tradeTickets} tickets`)
	if (offer.collections?.length) parts.push(`${offer.collections.length} coll.`)
	if (offer.boosters?.length) parts.push(`${offer.boosters.reduce((s, b) => s + (Number(b.amount) || 0), 0)} boosters`)
	if (offer.cards?.length) parts.push(`${offer.cards.reduce((s, c) => s + (Number(c.amount) || 0), 0)} cartes`)
	return parts.join(', ') || 'Vide'
}

export function rarityLabel(rarity) {
	const map = { common: 'Classique', silver: 'Silver', golden: 'Golden', foil: 'Foil' }
	return map[rarity] || rarity || 'Classique'
}

export const RARITY_OPTIONS = [
	{ id: 'common', name: 'Classique' },
	{ id: 'silver', name: 'Silver' },
	{ id: 'golden', name: 'Golden' },
	{ id: 'foil', name: 'Foil' },
]
